import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import {
  ArrowLeft,
  ArrowDown,
  ArrowLeftRight,
  ArrowRight,
  ArrowUp,
  Clock3,
  Copy,
  Database,
  Eye,
  EyeOff,
  FileCode2,
  Film,
  Grid3X3,
  History,
  Image as ImageIcon,
  Info,
  Layers3,
  Library,
  Lock,
  Maximize,
  Monitor,
  MousePointer2,
  Paintbrush,
  Play,
  Plus,
  RadioTower,
  Redo2,
  RotateCcw,
  Save,
  Search,
  Shapes,
  SlidersHorizontal,
  Sparkles,
  Square,
  Timer,
  Trash2,
  Type,
  Undo2,
  Unlock,
  WandSparkles,
  ZoomIn,
} from 'lucide-react'
import { cgAssetsApi, cgFontsApi, cgTemplatesApi, logoProfilesApi } from '../../lib/api.js'
import { openSettingsTab } from '../../lib/appNavigation.js'
import { useHasPermission } from '../../store/index.js'
import { CgSaveTracker, cgDraftKey, readCgDraft, cgSaveError, validCgDesign, type CgEditorSnapshot } from './cgEditorState.js'
import { ModalShell } from '../common/ModalShell.js'
import { CgLayerOrderDialog } from './CgLayerOrderDialog.js'
import { overlayVideoLayer } from './cgLayerOrder.js'
import { Button } from '../ui/button.js'
import { ColorWell } from './ColorWell.js'
import { CgTemplateThumbnail } from './CgTemplateThumbnail.js'
import { elementVisibleAt, timingSummary, visibilityWindows } from './cgElementTiming.js'
import { CgTemplateSettingsDialog, type CgSettingsDraft } from './CgTemplateSettingsDialog.js'
import { isoToParisDateTimeInput, parisDateTimeInputToIso } from '../../lib/paris-date-time.js'
import type {
  CgDesignerAnimation,
  CgDesignerCounterFormat,
  CgDesignerCounterMode,
  CgDesignerDesign,
  CgDesignerEasing,
  CgDesignerElement,
  CgDesignerElementType,
  CgAsset,
  CgFont,
  CgLayerOrderResult,
  CgTemplate,
} from '../../types/index.js'

type TransformMode = 'move' | 'resize' | 'rotate'
type PreviewMode = 'enter' | 'exit'

interface VariableOption {
  value: string
  label: string
  group: string
  sample: string
}

const VARIABLE_OPTIONS: VariableOption[] = [
  { value: 'program.current.title', label: 'Titre', group: 'Programme en cours', sample: 'Le grand journal' },
  { value: 'program.current.titleShort', label: 'Titre court', group: 'Programme en cours', sample: 'Grand journal' },
  { value: 'program.current.summary', label: 'Resume', group: 'Programme en cours', sample: 'Actualite et invites en direct' },
  { value: 'program.current.genre', label: 'Genre', group: 'Programme en cours', sample: 'Magazine' },
  { value: 'program.current.presenter', label: 'Presentateur', group: 'Programme en cours', sample: 'Camille Martin' },
  { value: 'program.current.production', label: 'Production', group: 'Programme en cours', sample: 'LMtv Sarthe' },
  { value: 'program.current.year', label: 'Annee', group: 'Programme en cours', sample: '2026' },
  { value: 'program.current.firstAirDate', label: 'Date de premiere diffusion', group: 'Programme en cours', sample: '12/09/2026' },
  { value: 'program.current.startTime', label: 'Heure de debut', group: 'Programme en cours', sample: '20:30' },
  { value: 'program.current.endTime', label: 'Heure de fin', group: 'Programme en cours', sample: '21:25' },
  { value: 'program.current.startAt', label: 'Date et heure de debut', group: 'Programme en cours', sample: new Date(Date.now() - 1800000).toISOString() },
  { value: 'program.current.endAt', label: 'Date et heure de fin', group: 'Programme en cours', sample: new Date(Date.now() + 1800000).toISOString() },
  { value: 'program.current.imageUrl', label: 'Image programme', group: 'Programme en cours', sample: '' },
  { value: 'program.next.title', label: 'Titre', group: 'Programme suivant', sample: 'Meteo' },
  { value: 'program.next.titleShort', label: 'Titre court', group: 'Programme suivant', sample: 'Meteo' },
  { value: 'program.next.summary', label: 'Resume', group: 'Programme suivant', sample: 'Les previsions de votre region' },
  { value: 'program.next.firstAirDate', label: 'Date de premiere diffusion', group: 'Programme suivant', sample: '12/09/2026' },
  { value: 'program.next.startTime', label: 'Heure de debut', group: 'Programme suivant', sample: '21:25' },
  { value: 'program.next.startAt', label: 'Date et heure de debut', group: 'Programme suivant', sample: new Date(Date.now() + 3600000).toISOString() },
  { value: 'program.next.endAt', label: 'Date et heure de fin', group: 'Programme suivant', sample: new Date(Date.now() + 7200000).toISOString() },
  { value: 'program.next.imageUrl', label: 'Image programme', group: 'Programme suivant', sample: '' },
  { value: 'channel.name', label: 'Nom de la chaine', group: 'Chaine', sample: 'LMtv Sarthe' },
  { value: 'channel.logoUrl', label: 'Logo de la chaine', group: 'Chaine', sample: '' },
  { value: 'event.title', label: 'Titre', group: 'Evenement personnalise (Diffusion)', sample: 'Finale en direct' },
  { value: 'event.startAt', label: 'Date de debut', group: 'Evenement personnalise (Diffusion)', sample: new Date(Date.now() + 7 * 86400000).toISOString() },
  { value: 'event.endAt', label: 'Date de fin', group: 'Evenement personnalise (Diffusion)', sample: new Date(Date.now() + 7 * 86400000 + 7200000).toISOString() },
  { value: 'clock.now', label: 'Heure systeme', group: 'Horloge', sample: new Date().toISOString() },
]

const COUNTER_VARIABLES = new Set([
  'program.current.startAt',
  'program.current.endAt',
  'program.next.startAt',
  'program.next.endAt',
  'event.startAt',
  'event.endAt',
  'clock.now',
])

const ANIMATION_OPTIONS: Array<{ value: CgDesignerAnimation; label: string }> = [
  { value: 'NONE', label: 'Aucune' },
  { value: 'FADE', label: 'Fondu' },
  { value: 'SLIDE_LEFT', label: 'Glissement gauche' },
  { value: 'SLIDE_RIGHT', label: 'Glissement droite' },
  { value: 'SLIDE_UP', label: 'Glissement haut' },
  { value: 'SLIDE_DOWN', label: 'Glissement bas' },
  { value: 'SCALE', label: 'Echelle' },
  { value: 'POP', label: 'Impact' },
  { value: 'WIPE_LEFT', label: 'Volet gauche' },
  { value: 'WIPE_RIGHT', label: 'Volet droite' },
  { value: 'ROTATE', label: 'Rotation' },
  { value: 'BLUR', label: 'Flou' },
]

const EASING_OPTIONS: Array<{ value: CgDesignerEasing; label: string }> = [
  { value: 'LINEAR', label: 'Lineaire' },
  { value: 'EASE_IN', label: 'Acceleration' },
  { value: 'EASE_OUT', label: 'Deceleration' },
  { value: 'EASE_IN_OUT', label: 'Fluide' },
  { value: 'CIRC_OUT', label: 'Dynamique' },
]

const FONT_OPTIONS = [
  'Arial, Helvetica, sans-serif',
  'Verdana, Geneva, sans-serif',
  'Trebuchet MS, sans-serif',
  'Georgia, serif',
  'Impact, sans-serif',
  'Cascadia Code, Consolas, monospace',
]

const TYPE_LABELS: Record<CgDesignerElementType, string> = {
  TEXT: 'Texte',
  SHAPE: 'Forme',
  IMAGE: 'Image',
  VIDEO: 'Video',
  COUNTER: 'Compteur',
}


function uid(prefix: string): string {
  return `cg-${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function defaultElement(type: CgDesignerElementType): CgDesignerElement {
  const base: CgDesignerElement = {
    id: uid(type.toLowerCase()),
    type,
    name: TYPE_LABELS[type],
    x: 240,
    y: 420,
    width: 640,
    height: 120,
    rotation: 0,
    visible: true,
    locked: false,
    color: 'white',
    background: 'transparent',
    fontSize: 54,
    fontWeight: 700,
    fontFamily: FONT_OPTIONS[0],
    textAlign: 'left',
    letterSpacing: 0,
    lineHeight: 1.05,
    borderRadius: 0,
    borderWidth: 0,
    borderColor: 'transparent',
    opacity: 1,
    shadowColor: 'transparent',
    shadowBlur: 0,
    shadowX: 0,
    shadowY: 0,
    enterAnimation: 'SLIDE_LEFT',
    exitAnimation: 'FADE',
    easing: 'EASE_OUT',
    delayMs: 0,
    durationMs: 500,
    exitDelayMs: 0,
    exitDurationMs: 300,
  }
  if (type === 'TEXT') return { ...base, text: 'Nouveau titrage', dataKey: 'program.current.title', scroll: false, scrollDirection: 'left', scrollSpeedPxSec: 120, scrollGapPx: 80 }
  if (type === 'SHAPE') return { ...base, name: 'Rectangle', width: 720, height: 160, background: 'rgba(0, 0, 0, 0.82)', enterAnimation: 'WIPE_LEFT' }
  if (type === 'IMAGE') return { ...base, width: 360, height: 240, imageUrl: '', imageFit: 'cover', dataKey: '', enterAnimation: 'FADE' }
  if (type === 'VIDEO') return { ...base, width: 640, height: 360, videoUrl: '', imageFit: 'contain', videoLoop: true, videoPlaybackRate: 1, videoHoldFrame: 0, videoFrameRate: 25, videoResumeBeforeEndMs: 1000, dataKey: '', enterAnimation: 'FADE' }
  return {
    ...base,
    name: 'Compte a rebours',
    width: 760,
    text: '7j 00:00:00',
    dataKey: '',
    counterMode: 'COUNTDOWN',
    counterFormat: 'D_H_M_S',
    counterTarget: new Date(Date.now() + 7 * 86400000).toISOString(),
    counterPrefix: '',
    counterSuffix: '',
  }
}

function blankDesign(): CgDesignerDesign {
  return {
    width: 1920,
    height: 1080,
    background: 'transparent',
    fps: 25,
    durationMs: 10000,
    gridSize: 10,
    safeArea: 5,
    elements: [],
  }
}

function normalizeDesign(raw: unknown): CgDesignerDesign {
  if (!raw || typeof raw !== 'object') return blankDesign()
  const source = raw as Omit<Partial<CgDesignerDesign>, 'elements'> & { elements?: Array<Omit<CgDesignerElement, 'type'> & { type: CgDesignerElementType | 'BOX' }> }
  return {
    width: Number(source.width) || 1920,
    height: Number(source.height) || 1080,
    background: source.background || 'transparent',
    fps: Number(source.fps) || 25,
    durationMs: Number(source.durationMs) || 10000,
    gridSize: Number(source.gridSize) || 10,
    safeArea: source.safeArea == null ? 5 : Number(source.safeArea),
    elements: (source.elements ?? []).map(element => ({
      ...defaultElement(element.type === 'BOX' ? 'SHAPE' : element.type),
      ...element,
      type: element.type === 'BOX' ? 'SHAPE' : element.type,
      enterAnimation: element.enterAnimation ?? element.animation ?? 'NONE',
    })),
  }
}

function readSample(key: string | undefined): string {
  return VARIABLE_OPTIONS.find(option => option.value === key)?.sample ?? ''
}

function pad(value: number): string {
  return String(Math.max(0, Math.floor(value))).padStart(2, '0')
}

function formatCounter(element: CgDesignerElement, now: number): string {
  if (element.counterMode === 'CLOCK') {
    const options: Intl.DateTimeFormatOptions = element.counterFormat === 'HH_MM'
      ? { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }
      : element.counterFormat === 'MM_SS'
        ? { timeZone: 'Europe/Paris', minute: '2-digit', second: '2-digit' }
        : { timeZone: 'Europe/Paris' }
    return new Date(now).toLocaleTimeString('fr-FR', options)
  }
  const targetValue = readSample(element.dataKey) || element.counterTarget || ''
  const target = new Date(targetValue).getTime()
  if (!Number.isFinite(target)) return element.text || ''
  const difference = element.counterMode === 'COUNTUP' ? now - target : target - now
  const total = Math.max(0, Math.floor(difference / 1000))
  const days = Math.floor(total / 86400)
  const hours = Math.floor((total % 86400) / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  let value = ''
  if (element.counterFormat === 'DAYS') value = `J-${Math.ceil(total / 86400)}`
  else if (element.counterFormat === 'MINUTES') value = `${Math.ceil(total / 60)} min`
  else if (element.counterFormat === 'DATE_TIME') value = new Date(target).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })
  else if (element.counterFormat === 'HH_MM_SS') value = `${pad(Math.floor(total / 3600))}:${pad(minutes)}:${pad(seconds)}`
  else if (element.counterFormat === 'HH_MM') value = `${pad(Math.floor(total / 3600))}:${pad(minutes)}`
  else if (element.counterFormat === 'MM_SS') value = `${pad(Math.floor(total / 60))}:${pad(seconds)}`
  else value = `${days}j ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
  return `${element.counterPrefix || ''}${value}${element.counterSuffix || ''}`
}

function elementContent(element: CgDesignerElement, now: number): string {
  if (element.type === 'COUNTER') return formatCounter(element, now)
  if (element.dataKey) {
    // Comme a l'antenne : prefixe et suffixe n'encadrent qu'une vraie valeur.
    const sample = readSample(element.dataKey)
    return sample ? `${element.textPrefix || ''}${sample}${element.textSuffix || ''}` : element.text || ''
  }
  return element.text || ''
}

const CounterPreview = memo(function CounterPreview({ element, active, scrubMs }: { element: CgDesignerElement; active: boolean; scrubMs: number | null }) {
  const [now, setNow] = useState(Date.now())
  const origin = useRef(Date.now())
  useEffect(() => {
    if (!active || scrubMs !== null) return
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [active, scrubMs])
  return <>{formatCounter(element, scrubMs === null ? now : origin.current + scrubMs)}</>
})

interface MarqueeTextProps {
  text: string
  direction: 'left' | 'right'
  speedPxSec: number
  gapPx: number
  zoom: number
  active: boolean
  scrubMs: number | null
}

// Meme technique que le rendu CasparCG reel (cg-template-designer.service.ts) :
// deux copies du texte mesurees dans le DOM, translatees d'une largeur de
// copie + l'ecart. Le deplacement est pilote en JS via requestAnimationFrame
// plutot que par une animation CSS a base de variable + calc(), pour rester
// coherent avec le rendu CEF de CasparCG qui n'applique pas cette combinaison
// de facon fiable.
const MarqueeText = memo(function MarqueeText({ text, direction, speedPxSec, gapPx, zoom, active, scrubMs }: MarqueeTextProps) {
  const trackRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const track = trackRef.current
    const copy = track?.querySelector<HTMLElement>('.cg-marquee-preview-copy')
    if (!track || !copy) return
    const speed = Math.max(10, speedPxSec) * zoom
    const distance = copy.getBoundingClientRect().width + gapPx * zoom
    if (!(distance > 0)) return
    const reverse = direction === 'right'
    if (scrubMs !== null) { track.style.transform = `translateX(${(direction === 'right' ? 1 : -1) * (speed * scrubMs / 1000 % distance)}px)`; return }
    if (!active) return
    let offset = 0
    let last = performance.now()
    let raf = 0
    const tick = (now: number) => {
      const dt = (now - last) / 1000
      last = now
      offset = (offset + speed * dt) % distance
      track.style.transform = `translateX(${reverse ? offset : -offset}px)`
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [text, direction, speedPxSec, gapPx, zoom, active, scrubMs])

  return (
    <span className={`cg-marquee-preview dir-${direction}`}>
      <span ref={trackRef} className="cg-marquee-preview-track" style={{ gap: gapPx * zoom }}>
        <span className="cg-marquee-preview-copy">{text}</span>
        <span className="cg-marquee-preview-copy" aria-hidden="true">{text}</span>
      </span>
    </span>
  )
})

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function easingCss(value: CgDesignerEasing | undefined): string {
  if (value === 'LINEAR') return 'linear'
  if (value === 'EASE_IN') return 'cubic-bezier(.4, 0, 1, 1)'
  if (value === 'EASE_IN_OUT') return 'cubic-bezier(.65, 0, .35, 1)'
  if (value === 'CIRC_OUT') return 'cubic-bezier(0, .55, .45, 1)'
  return 'cubic-bezier(.2, .8, .2, 1)'
}

function animationSlug(value: CgDesignerAnimation | undefined): string {
  return (value || 'NONE').toLowerCase().replace(/_/g, '-')
}

interface ToolbarProps {
  name: string
  slug: string
  saving: boolean
  canUndo: boolean
  canRedo: boolean
  canUse: boolean
  canTest: boolean
  /** Le compte voit Réglages › Canal de test (réglages techniques). */
  canOpenTestChannel: boolean
  message: string
  onName: (value: string) => void
  onSlug: (value: string) => void
  onBack: () => void
  onNew: () => void
  onSave: () => void
  onReload: () => void
  onHistory: () => void
  onDuplicateProject: () => void
  onDeleteProject: () => void
  onUndo: () => void
  onRedo: () => void
  onUse: () => void
  onOpenTestChannel: () => void
}

const StudioToolbar = memo(function StudioToolbar(props: ToolbarProps) {
  return (
    <div className="cg-studio-toolbar">
      <div className="cg-project-fields">
        <button type="button" className="cg-icon-button" onClick={props.onBack} title="Retour aux templates" aria-label="Retour aux templates"><ArrowLeft size={15} /></button>
        <FileCode2 size={16} aria-hidden="true" />
        <input className="cg-project-name" value={props.name} onChange={event => props.onName(event.target.value)} maxLength={255} aria-label="Nom du template" />
        <input className="cg-project-slug" value={props.slug} onChange={event => props.onSlug(event.target.value)} placeholder="identifiant-html" maxLength={80} aria-label="Identifiant HTML" />
      </div>
      <div className="cg-toolbar-actions">
        <span className="cg-action-group">
          <button type="button" className="cg-icon-button" onClick={props.onNew} title="Nouveau template" aria-label="Nouveau template"><Plus size={15} /></button>
          <button type="button" className="cg-icon-button" onClick={props.onUndo} disabled={!props.canUndo} title="Annuler" aria-label="Annuler"><Undo2 size={15} /></button>
          <button type="button" className="cg-icon-button" onClick={props.onRedo} disabled={!props.canRedo} title="Rétablir" aria-label="Rétablir"><Redo2 size={15} /></button>
        </span>
        <button type="button" className="cg-icon-button" onClick={props.onDuplicateProject} title="Dupliquer le template" aria-label="Dupliquer le template"><Copy size={15} /></button>
        <button type="button" className="cg-icon-button is-danger" onClick={props.onDeleteProject} disabled={!props.canUse} title="Supprimer le template" aria-label="Supprimer le template"><Trash2 size={15} /></button>
        {props.canUse && <Button type="button" variant="outline" size="icon" onClick={props.onHistory} disabled={props.saving} aria-label="Versions enregistrées" title="Versions enregistrées"><History aria-hidden="true" /></Button>}
        {props.canUse && <Button type="button" variant="outline" size="icon" onClick={props.onReload} disabled={props.saving} aria-label="Recharger la version enregistrée" title="Recharger la version enregistrée"><RotateCcw aria-hidden="true" /></Button>}
        <button type="button" className="cg-button is-primary" onClick={props.onSave} disabled={props.saving || !props.name.trim()}><Save size={15} />{props.saving ? 'Enregistrement...' : 'Enregistrer'}</button>
        <button type="button" className="cg-button" onClick={props.onUse} disabled={!props.canUse}><RadioTower size={15} />Diffuser</button>
        {/* Le Studio ne teste plus : l'essai se fait dans Réglages › Canal de
            test (ou depuis l'onglet Diffusion), sur la version enregistrée. */}
        {props.canUse && props.canOpenTestChannel && (
          <button
            type="button"
            className="cg-button cg-test-channel-link"
            onClick={props.onOpenTestChannel}
            disabled={!props.canTest}
            title={props.canTest ? 'Ouvrir le canal de test (canal 2), jamais la diffusion' : 'Enregistrez le template : le canal de test montre la version enregistrée'}
          >
            Voir sur le canal de test<ArrowRight size={15} aria-hidden="true" />
          </button>
        )}
      </div>
      {props.message && <span role="status" className="cg-toolbar-message"><Info size={13} />{props.message}</span>}
    </div>
  )
})

interface TemplatesProps {
  templates: CgTemplate[]
  templateId: string | null
  canResume: boolean
  onLoad: (template: CgTemplate) => void
  onNew: () => void
  onResume: () => void
  onSettings: (template: CgTemplate) => void
  /** Ouvre la superposition à l'antenne ; absent tant qu'il n'y a pas deux habillages. */
  onLayerOrder?: () => void
}

function templateSlugFromPath(htmlPath: string): string {
  const fileName = htmlPath.replace(/\\/g, '/').split('/').pop() ?? ''
  return fileName.replace(/\.html?$/i, '')
}

function isLogoTemplatePath(htmlPath: string): boolean {
  return htmlPath.replace(/\\/g, '/').replace(/^\.\//, '').startsWith('trafficplay-custom/logos/')
}

const TemplatesPanel = memo(function TemplatesPanel({ templates, templateId, canResume, onLoad, onNew, onResume, onSettings, onLayerOrder }: TemplatesProps) {
  const [query, setQuery] = useState('')
  const filteredTemplates = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('fr-FR')
    return normalized ? templates.filter(template => template.name.toLocaleLowerCase('fr-FR').includes(normalized)) : templates
  }, [query, templates])

  return (
    <section className="cg-template-home">
      <header className="cg-template-home-toolbar">
        <div className="cg-template-home-summary"><FileCode2 size={16} /><strong>Templates</strong><span>{templates.length}</span></div>
        <label className="cg-template-home-search"><Search size={14} /><input value={query} onChange={event => setQuery(event.target.value)} placeholder="Rechercher" aria-label="Rechercher un template" /></label>
        {canResume && <button type="button" className="cg-button" onClick={onResume}><Paintbrush size={14} />Reprendre</button>}
        {onLayerOrder && <button type="button" className="cg-button" onClick={onLayerOrder} title="Choisir quel habillage s’affiche au-dessus des autres"><Layers3 size={14} aria-hidden="true" />Superposition</button>}
        <button type="button" className="cg-button is-primary" onClick={onNew}><Plus size={14} />Nouveau template</button>
      </header>
      <div className="cg-template-tiles">
        {filteredTemplates.map(template => (
          <article key={template.id} className={`cg-template-tile${templateId === template.id ? ' is-current' : ''}${template.active ? '' : ' is-muted'}`}>
            <button
              type="button"
              className="cg-template-tile-preview"
              onClick={() => onLoad(template)}
              title={`Ouvrir ${template.name}`}
              aria-label={`Ouvrir ${template.name}`}
            >
              <CgTemplateThumbnail template={template} />
            </button>
            <div className="cg-template-tile-body">
              <strong title={template.name}>{template.name}</strong>
              <small title={template.htmlPath}>{templateSlugFromPath(template.htmlPath)}</small>
            </div>
            <div className="cg-template-tile-badges">
              {template.manualOnGrid && <span className="cg-template-tile-badge"><RadioTower size={10} />Grille</span>}
              {template.cgLayer !== null && <span className="cg-template-tile-badge" title="Couche vidéo CasparCG : plus elle est haute, plus l’habillage est au-dessus"><Layers3 size={10} />Couche {overlayVideoLayer(template.cgLayer)}</span>}
            </div>
            <div className="cg-template-tile-actions">
              <button type="button" className="cg-icon-button" onClick={() => onSettings(template)} title="Réglages du modèle" aria-label={`Réglages de ${template.name}`}>
                <SlidersHorizontal size={13} />
              </button>
              <button type="button" className="cg-icon-button" onClick={() => onLoad(template)} title="Ouvrir dans le studio" aria-label={`Ouvrir ${template.name}`}>
                <ArrowRight size={13} />
              </button>
            </div>
          </article>
        ))}
        {filteredTemplates.length === 0 && <div className="cg-template-home-empty"><FileCode2 size={24} /><span>{query ? 'Aucun resultat' : 'Aucun template'}</span></div>}
      </div>
    </section>
  )
})

interface ResourcesProps {
  elements: CgDesignerElement[]
  selectedId: string | null
  onAdd: (type: CgDesignerElementType) => void
  onSelect: (id: string) => void
  onToggleVisible: (id: string) => void
  onToggleLocked: (id: string) => void
  onReorder: (id: string, direction: -1 | 1) => void
}

const ResourcesPanel = memo(function ResourcesPanel(props: ResourcesProps) {
  return (
    <aside className="cg-studio-resources">
      <section>
        <div className="cg-panel-heading"><strong><Shapes size={14} />Éléments</strong></div>
        <div className="cg-tool-grid">
          <button type="button" onClick={() => props.onAdd('TEXT')} title="Ajouter un texte"><Type size={19} /><span>Texte</span></button>
          <button type="button" onClick={() => props.onAdd('SHAPE')} title="Ajouter une forme"><Square size={19} /><span>Forme</span></button>
          <button type="button" onClick={() => props.onAdd('IMAGE')} title="Ajouter une image"><ImageIcon size={19} /><span>Image</span></button>
          <button type="button" onClick={() => props.onAdd('VIDEO')} title="Ajouter une video"><Film size={19} /><span>Video</span></button>
          <button type="button" onClick={() => props.onAdd('COUNTER')} title="Ajouter un compteur"><Timer size={19} /><span>Compteur</span></button>
        </div>
      </section>

      <section className="cg-layer-section">
        <div className="cg-panel-heading"><strong><Layers3 size={14} />Calques</strong><small>{props.elements.length}</small></div>
        <div className="cg-layer-list">
          {[...props.elements].reverse().map(element => (
            <div key={element.id} className={`cg-layer-row${props.selectedId === element.id ? ' is-selected' : ''}`}>
              <button type="button" className="cg-layer-main" onClick={() => props.onSelect(element.id)}>
                <span>{element.type === 'TEXT' ? <Type size={13} /> : element.type === 'SHAPE' ? <Square size={13} /> : element.type === 'IMAGE' ? <ImageIcon size={13} /> : element.type === 'VIDEO' ? <Film size={13} /> : <Timer size={13} />}</span>
                <strong>{element.name}</strong>
              </button>
              <button type="button" className="cg-layer-action" onClick={() => props.onToggleVisible(element.id)} title={element.visible === false ? 'Afficher' : 'Masquer'} aria-label={element.visible === false ? 'Afficher' : 'Masquer'}>{element.visible === false ? <EyeOff size={12} /> : <Eye size={12} />}</button>
              <button type="button" className="cg-layer-action" onClick={() => props.onToggleLocked(element.id)} title={element.locked ? 'Déverrouiller' : 'Verrouiller'} aria-label={element.locked ? 'Déverrouiller' : 'Verrouiller'}>{element.locked ? <Lock size={12} /> : <Unlock size={12} />}</button>
              <button type="button" className="cg-layer-action" onClick={() => props.onReorder(element.id, 1)} title="Monter" aria-label="Monter"><ArrowUp size={12} /></button>
              <button type="button" className="cg-layer-action" onClick={() => props.onReorder(element.id, -1)} title="Descendre" aria-label="Descendre"><ArrowDown size={12} /></button>
            </div>
          ))}
          {props.elements.length === 0 && <div className="cg-empty">Composition vide</div>}
        </div>
      </section>
    </aside>
  )
})

interface CanvasElementProps {
  element: CgDesignerElement
  zoom: number
  selected: boolean
  active: boolean
  scrubMs: number | null
  previewRun: number
  previewMode: PreviewMode
  templateDurationMs: number
  templateFrameRate: number
  onSelect: (id: string) => void
  onTransform: (id: string, mode: TransformMode, event: ReactPointerEvent) => void
}

interface PreviewVideoProps {
  active: boolean
  scrubMs: number | null
  element: CgDesignerElement
  src: string
  previewRun: number
  previewMode: PreviewMode
  templateDurationMs: number
  templateFrameRate: number
}

type FrameAwareVideo = HTMLVideoElement & {
  requestVideoFrameCallback?: (callback: (now: number, metadata: { mediaTime: number }) => void) => number
  cancelVideoFrameCallback?: (handle: number) => void
}

const PreviewVideo = memo(function PreviewVideo({ element, src, previewRun, previewMode, templateDurationMs, templateFrameRate, active, scrubMs }: PreviewVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const video = videoRef.current as FrameAwareVideo | null
    if (!video) return

    const holdFrame = Math.max(0, Math.floor(Number(element.videoHoldFrame) || 0))
    const frameRate = Math.max(1, Number(element.videoFrameRate) || templateFrameRate || 25)
    const holdAt = Math.max(0, (holdFrame - 1) / frameRate)
    const resumeBeforeEndMs = Math.max(0, Number(element.videoResumeBeforeEndMs) || 0)
    let stopped = false
    let held = false
    let outroStarted = false
    let frameCallbackId: number | null = null
    let animationFrameId: number | null = null
    let resumeTimer: number | null = null

    const cancelFrameWatch = () => {
      if (frameCallbackId != null && video.cancelVideoFrameCallback) video.cancelVideoFrameCallback(frameCallbackId)
      if (animationFrameId != null) cancelAnimationFrame(animationFrameId)
      frameCallbackId = null
      animationFrameId = null
    }
    const freezeIfReady = (mediaTime: number) => {
      if (stopped || held || outroStarted) return true
      if (mediaTime + (0.25 / frameRate) < holdAt) return false
      held = true
      video.pause()
      video.currentTime = holdAt
      cancelFrameWatch()
      return true
    }
    const watchFrame = () => {
      if (video.requestVideoFrameCallback) {
        const onFrame = (_now: number, metadata: { mediaTime: number }) => {
          if (!freezeIfReady(metadata.mediaTime)) frameCallbackId = video.requestVideoFrameCallback?.(onFrame) ?? null
        }
        frameCallbackId = video.requestVideoFrameCallback(onFrame)
        return
      }
      const onAnimationFrame = () => {
        if (!freezeIfReady(video.currentTime)) animationFrameId = requestAnimationFrame(onAnimationFrame)
      }
      animationFrameId = requestAnimationFrame(onAnimationFrame)
    }
    const resumeOutro = () => {
      if (stopped || outroStarted) return
      outroStarted = true
      held = false
      cancelFrameWatch()
      video.loop = false
      void video.play().catch(() => undefined)
    }
    const start = () => {
      if (!active || scrubMs !== null) {
        video.pause()
        if (scrubMs !== null) {
          const seconds = scrubMs / 1000 * (element.videoPlaybackRate ?? 1)
          const outroStart = Math.max(0, templateDurationMs - resumeBeforeEndMs) / 1000
          const time = holdFrame > 0
            ? previewMode === 'exit' ? holdAt + seconds : scrubMs / 1000 < outroStart ? Math.min(seconds, holdAt) : holdAt + (scrubMs / 1000 - outroStart) * (element.videoPlaybackRate ?? 1)
            : seconds
          video.currentTime = Number.isFinite(video.duration) && video.duration > 0
            ? (holdFrame === 0 && element.videoLoop !== false ? time % video.duration : Math.min(time, video.duration)) : time
        }
        return
      }
      video.muted = true
      video.loop = holdFrame === 0 && element.videoLoop !== false
      video.playbackRate = element.videoPlaybackRate || 1
      video.currentTime = previewMode === 'exit' && holdFrame > 0 ? holdAt : 0
      if (previewMode === 'exit' && holdFrame > 0) {
        resumeOutro()
        return
      }
      void video.play().catch(() => undefined)
      if (holdFrame > 0) {
        watchFrame()
        resumeTimer = window.setTimeout(resumeOutro, Math.max(0, templateDurationMs - resumeBeforeEndMs))
      }
    }

    if (video.readyState >= 1) start()
    else video.addEventListener('loadedmetadata', start, { once: true })

    return () => {
      stopped = true
      video.removeEventListener('loadedmetadata', start)
      cancelFrameWatch()
      if (resumeTimer != null) window.clearTimeout(resumeTimer)
      video.pause()
    }
  }, [element.videoHoldFrame, element.videoFrameRate, element.videoLoop, element.videoPlaybackRate, element.videoResumeBeforeEndMs, previewMode, previewRun, src, templateDurationMs, templateFrameRate, active, scrubMs])

  return <video ref={videoRef} src={src} autoPlay={active && scrubMs === null} muted loop={(element.videoHoldFrame ?? 0) <= 0 && element.videoLoop !== false} playsInline style={{ objectFit: element.imageFit || 'contain' }} />
})

const CanvasElement = memo(function CanvasElement({ element, zoom, selected, active, scrubMs, previewRun, previewMode, templateDurationMs, templateFrameRate, onSelect, onTransform }: CanvasElementProps) {
  if (element.visible === false) return null
  const isMarquee = element.type === 'TEXT' && Boolean(element.scroll)
  const justifyContent = isMarquee ? 'flex-start' : element.textAlign === 'center' ? 'center' : element.textAlign === 'right' ? 'flex-end' : 'flex-start'
  const animation = previewMode === 'enter' ? element.enterAnimation : element.exitAnimation
  // Tete de lecture hors de la fenetre d'affichage : l'element est estompe,
  // il resterait masque a l'antenne a cet instant.
  const timedHidden = previewMode === 'enter' && scrubMs !== null && (element.visibleForMs ?? 0) > 0
    && scrubMs >= (element.delayMs ?? 0) && !elementVisibleAt(element, scrubMs)
  const className = `cg-canvas-element cg-element-${element.type.toLowerCase()} cg-preview-${previewMode}-${animationSlug(animation)}${selected ? ' is-selected' : ''}${timedHidden ? ' is-timed-hidden' : ''}`
  // La box garde bordure et ombre. En plus, sur texte/compteur, des champs
  // dédiés cernent et ombrent les glyphes (text-stroke / text-shadow), sans
  // toucher la box. Cf. renderElementCss côté backend.
  const isText = element.type === 'TEXT' || element.type === 'COUNTER'
  const textStrokeWidth = (element.textStrokeWidth || 0) * zoom
  const hasTextShadow = Boolean(element.textShadowX || element.textShadowY || element.textShadowBlur)
  const textShadowValue = `${(element.textShadowX || 0) * zoom}px ${(element.textShadowY || 0) * zoom}px ${(element.textShadowBlur || 0) * zoom}px ${element.textShadowColor || 'transparent'}`
  const style: CSSProperties = {
    left: element.x * zoom,
    top: element.y * zoom,
    width: element.width * zoom,
    height: element.height * zoom,
    transform: `rotate(${element.rotation || 0}deg)`,
    color: element.color,
    background: element.background,
    borderRadius: (element.borderRadius || 0) * zoom,
    borderWidth: (element.borderWidth || 0) * zoom,
    borderColor: element.borderColor,
    opacity: element.opacity,
    fontSize: (element.fontSize || 42) * zoom,
    fontWeight: element.fontWeight,
    fontFamily: element.fontFamily,
    textAlign: element.textAlign,
    justifyContent,
    letterSpacing: (element.letterSpacing || 0) * zoom,
    lineHeight: element.lineHeight,
    boxShadow: `${(element.shadowX || 0) * zoom}px ${(element.shadowY || 0) * zoom}px ${(element.shadowBlur || 0) * zoom}px ${element.shadowColor || 'transparent'}`,
    textShadow: isText && hasTextShadow ? textShadowValue : undefined,
    WebkitTextStroke: isText && textStrokeWidth > 0 ? `${textStrokeWidth}px ${element.textStrokeColor || 'transparent'}` : undefined,
    paintOrder: isText && textStrokeWidth > 0 ? 'stroke fill' : undefined,
    animationDuration: `${previewMode === 'enter' ? element.durationMs || 450 : element.exitDurationMs || 300}ms`,
    animationDelay: `${(previewMode === 'enter' ? element.delayMs ?? 0 : element.exitDelayMs ?? 0) - (scrubMs ?? 0)}ms`,
    animationPlayState: scrubMs !== null || !active ? 'paused' : 'running',
    animationTimingFunction: easingCss(element.easing),
  }
  const imageUrl = element.dataKey ? readSample(element.dataKey) || element.imageUrl : element.imageUrl
  const videoUrl = element.dataKey ? readSample(element.dataKey) || element.videoUrl : element.videoUrl
  return (
    <div
      key={`${element.id}-${previewRun}`}
      className={className}
      style={style}
      onPointerDown={event => {
        onSelect(element.id)
        if (!element.locked) onTransform(element.id, 'move', event)
      }}
      onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onSelect(element.id) } }}
      role="button"
      tabIndex={0}
      aria-label={element.name}
    >
      {element.type === 'IMAGE' ? (
        imageUrl ? <img src={imageUrl} alt="" style={{ objectFit: element.imageFit || 'cover' }} /> : <span className="cg-image-placeholder"><ImageIcon size={22} />Image</span>
      ) : element.type === 'VIDEO' ? (
        videoUrl
          ? <PreviewVideo element={element} src={videoUrl} previewRun={previewRun} previewMode={previewMode} templateDurationMs={templateDurationMs} templateFrameRate={templateFrameRate} active={active} scrubMs={scrubMs} />
          : <span className="cg-image-placeholder"><Film size={22} />Video</span>
      ) : isMarquee ? (
        <MarqueeText text={elementContent(element, 0)} direction={element.scrollDirection === 'right' ? 'right' : 'left'} speedPxSec={element.scrollSpeedPxSec ?? 120} gapPx={element.scrollGapPx ?? 80} zoom={zoom} active={active} scrubMs={scrubMs} />
      ) : element.type === 'COUNTER' ? <CounterPreview element={element} active={active} scrubMs={scrubMs} /> : element.type !== 'SHAPE' ? elementContent(element, 0) : null}
      {selected && !element.locked && (
        <>
          <button type="button" className="cg-resize-handle" onPointerDown={event => { event.stopPropagation(); onTransform(element.id, 'resize', event) }} aria-label="Redimensionner" />
          <button type="button" className="cg-rotate-handle" onPointerDown={event => { event.stopPropagation(); onTransform(element.id, 'rotate', event) }} aria-label="Pivoter" />
        </>
      )}
    </div>
  )
})

interface CanvasProps {
  design: CgDesignerDesign
  zoom: number
  snapEnabled: boolean
  selectedId: string | null
  active: boolean
  scrubMs: number | null
  previewRun: number
  previewMode: PreviewMode
  onZoom: (zoom: number) => void
  onFit: () => void
  onSnap: (enabled: boolean) => void
  onSelect: (id: string | null) => void
  onTransform: (id: string, mode: TransformMode, event: ReactPointerEvent) => void
  workspaceRef: React.RefObject<HTMLDivElement>
}

const CanvasPanel = memo(function CanvasPanel(props: CanvasProps) {
  const gridSize = Math.max(1, (props.design.gridSize || 10) * props.zoom)
  const stageStyle = {
    width: props.design.width * props.zoom,
    height: props.design.height * props.zoom,
    backgroundColor: props.design.background || 'transparent',
    '--cg-grid-size': `${gridSize}px`,
  } as CSSProperties
  const safe = (props.design.safeArea || 0) / 100
  return (
    <section className="cg-canvas-panel">
      <div className="cg-canvas-toolbar">
        <span className="cg-canvas-meta"><Monitor size={13} />{props.design.width} × {props.design.height}</span>
        <label className="cg-check cg-toggle"><input type="checkbox" checked={props.snapEnabled} onChange={event => props.onSnap(event.target.checked)} /><span aria-hidden="true" /><Grid3X3 size={13} />Magnétisme</label>
        <button type="button" className="cg-icon-button" onClick={props.onFit} title="Ajuster à l’écran" aria-label="Ajuster à l’écran"><Maximize size={14} /></button>
        <ZoomIn size={13} aria-hidden="true" />
        <input type="range" min="10" max="100" value={Math.round(props.zoom * 100)} onChange={event => props.onZoom(Number(event.target.value) / 100)} aria-label="Zoom" />
        <span>{Math.round(props.zoom * 100)}%</span>
      </div>
      <div ref={props.workspaceRef} className="cg-canvas-workspace" onPointerDown={event => { if (event.target === event.currentTarget) props.onSelect(null) }}>
        <div className={`cg-canvas-stage${props.snapEnabled ? ' show-grid' : ''}`} style={stageStyle} onPointerDown={event => { if (event.target === event.currentTarget) props.onSelect(null) }}>
          {safe > 0 && (
            <div className="cg-safe-area" style={{ left: `${safe * 100}%`, top: `${safe * 100}%`, right: `${safe * 100}%`, bottom: `${safe * 100}%` }} />
          )}
          {props.design.elements.map(element => (
            <CanvasElement
              key={`${element.id}-${props.previewRun}`}
              element={element}
              zoom={props.zoom}
              selected={props.selectedId === element.id}
              active={props.active}
              scrubMs={props.scrubMs}
              previewRun={props.previewRun}
              previewMode={props.previewMode}
              templateDurationMs={props.design.durationMs || 10000}
              templateFrameRate={props.design.fps || 25}
              onSelect={props.onSelect}
              onTransform={props.onTransform}
            />
          ))}
        </div>
      </div>
    </section>
  )
})

interface FieldProps {
  label: string
  value: string | number
  onChange: (value: string) => void
  type?: string
  min?: number
  max?: number
  step?: number
}

const Field = memo(function Field({ label, value, onChange, type = 'text', min, max, step }: FieldProps) {
  const [draft, setDraft] = useState(String(value))
  const [error, setError] = useState('')
  useEffect(() => { setDraft(String(value)); setError('') }, [value])
  const change = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget
    setDraft(input.value)
    setError(input.validity.valid ? '' : input.validationMessage)
    if (type !== 'number' || (input.value !== '' && Number.isFinite(input.valueAsNumber))) onChange(input.value)
  }, [onChange, type])
  return <label className="cg-field"><span>{label}</span><input type={type} value={type === 'number' ? draft : value} required={type === 'number'} min={min} max={max} step={step ?? 'any'} aria-invalid={Boolean(error)} onChange={change} />{error && <small className="cg-settings-error">{error}</small>}</label>
})

interface SelectFieldProps {
  label: string
  value: string
  options: Array<{ value: string; label: string }>
  onChange: (value: string) => void
}

const SelectField = memo(function SelectField({ label, value, options, onChange }: SelectFieldProps) {
  return <label className="cg-field"><span>{label}</span><select value={value} onChange={event => onChange(event.target.value)}>{options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
})

interface ColorFieldProps {
  label: string
  value: string
  onChange: (value: string) => void
}

const ColorField = memo(function ColorField({ label, value, onChange }: ColorFieldProps) {
  return (
    <div className="cg-field"><span>{label}</span><ColorWell value={value} onChange={onChange} ariaLabel={`Modifier ${label.toLowerCase()}`} /></div>
  )
})

interface InspectorProps {
  element: CgDesignerElement | null
  design: CgDesignerDesign
  assets: CgAsset[]
  fonts: CgFont[]
  onElement: (patch: Partial<CgDesignerElement>) => void
  onDesign: (patch: Partial<CgDesignerDesign>) => void
  onDuplicate: () => void
  onDelete: () => void
}

const InspectorPanel = memo(function InspectorPanel({ element, design, assets, fonts, onElement, onDesign, onDuplicate, onDelete }: InspectorProps) {
  const knownVariable = element?.dataKey && VARIABLE_OPTIONS.some(option => option.value === element.dataKey)
  const groupedVariables = useMemo(() => {
    const groups = new Map<string, VariableOption[]>()
    const options = element?.type === 'VIDEO'
      ? []
      : element?.type === 'COUNTER'
        ? VARIABLE_OPTIONS.filter(option => COUNTER_VARIABLES.has(option.value))
        : VARIABLE_OPTIONS
    for (const option of options) groups.set(option.group, [...(groups.get(option.group) ?? []), option])
    return Array.from(groups.entries())
  }, [element?.type])
  const selectedAsset = useMemo(() => assets.find(asset => asset.id === element?.assetId) ?? null, [assets, element?.assetId])
  const compatibleAssets = useMemo(() => assets.filter(asset => element?.type === 'VIDEO'
    ? asset.mimeType.startsWith('video/')
    : asset.mimeType.startsWith('image/')), [assets, element?.type])
  const customFonts = fonts
  const selectMediaSource = useCallback((assetId: string) => {
    const asset = assets.find(item => item.id === assetId)
    if (element?.type === 'VIDEO') {
      onElement(asset ? { assetId: asset.id, videoUrl: asset.url, dataKey: '' } : { assetId: undefined, videoUrl: '' })
      return
    }
    // Choisir un asset est un choix de contenu fixe. Sans cette remise à zéro,
    // la variable historique `logoUrl` restait active sur les logos importés et
    // remplaçait silencieusement l'image choisie au moment de la diffusion.
    onElement(asset ? { assetId: asset.id, imageUrl: asset.url, dataKey: '' } : { assetId: undefined, imageUrl: '' })
  }, [assets, element?.type, onElement])

  if (!element) {
    return (
      <aside className="cg-studio-inspector">
        <div className="cg-panel-heading"><strong><SlidersHorizontal size={14} />Composition</strong></div>
        <div className="cg-inspector-scroll">
          <section className="cg-form-section">
            <h3><Monitor size={13} />Format</h3>
            <div className="cg-field-grid">
              <Field label="Largeur" type="number" value={design.width} onChange={value => onDesign({ width: Number(value) })} />
              <Field label="Hauteur" type="number" value={design.height} onChange={value => onDesign({ height: Number(value) })} />
              <Field label="FPS" type="number" value={design.fps || 25} onChange={value => onDesign({ fps: Number(value) })} />
              <Field label="Duree (ms)" type="number" value={design.durationMs || 10000} onChange={value => onDesign({ durationMs: Number(value) })} />
              <Field label="Grille (px)" type="number" value={design.gridSize || 10} onChange={value => onDesign({ gridSize: Number(value) })} />
              <Field label="Zone sure (%)" type="number" value={design.safeArea || 0} onChange={value => onDesign({ safeArea: Number(value) })} />
            </div>
            <ColorField label="Fond" value={design.background || 'transparent'} onChange={value => onDesign({ background: value })} />
          </section>
        </div>
      </aside>
    )
  }

  const variableValue = knownVariable ? element.dataKey || '' : element.dataKey ? '__custom__' : ''
  const videoHoldEnabled = element.type === 'VIDEO' && (element.videoHoldFrame ?? 0) > 0
  return (
    <aside className="cg-studio-inspector">
      <div className="cg-panel-heading">
        <strong><MousePointer2 size={14} />{element.name}</strong>
        <span className="cg-inspector-actions">
          <button type="button" className="cg-icon-button" onClick={onDuplicate} title="Dupliquer" aria-label="Dupliquer"><Copy size={14} /></button>
          <button type="button" className="cg-icon-button is-danger" onClick={onDelete} disabled={element.locked} title="Supprimer" aria-label="Supprimer"><Trash2 size={14} /></button>
        </span>
      </div>
      <fieldset disabled={element.locked} className="cg-inspector-scroll cg-inspector-fields">
        {element.locked && <p className="cg-settings-hint">Calque verrouillé — déverrouillez-le dans la liste des calques pour le modifier.</p>}
        <section className="cg-form-section">
          <h3><Layers3 size={13} />Calque</h3>
          <Field label="Nom" value={element.name} onChange={value => onElement({ name: value })} />
          <div className="cg-field-grid three">
            <Field label="X" type="number" value={Math.round(element.x)} onChange={value => onElement({ x: Number(value) })} />
            <Field label="Y" type="number" value={Math.round(element.y)} onChange={value => onElement({ y: Number(value) })} />
            <Field label="Rotation" type="number" value={element.rotation || 0} onChange={value => onElement({ rotation: Number(value) })} />
            <Field label="Largeur" type="number" value={Math.round(element.width)} onChange={value => onElement({ width: Number(value) })} />
            <Field label="Hauteur" type="number" value={Math.round(element.height)} onChange={value => onElement({ height: Number(value) })} />
            <Field label="Opacite" type="number" min={0} max={1} step={0.05} value={element.opacity ?? 1} onChange={value => onElement({ opacity: Number(value) })} />
          </div>
        </section>

        {element.type !== 'SHAPE' && (
          <section className="cg-form-section">
            <h3><Database size={13} />Contenu dynamique</h3>
            <label className="cg-field"><span>Variable</span>
              <select value={variableValue} onChange={event => onElement({ dataKey: event.target.value === '__custom__' ? 'custom.value' : event.target.value })}>
                <option value="">Valeur fixe</option>
                {groupedVariables.map(([group, options]) => (
                  <optgroup key={group} label={group}>{options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</optgroup>
                ))}
                <option value="__custom__">Variable personnalisee</option>
              </select>
            </label>
            {variableValue === '__custom__' && <Field label="Cle" value={element.dataKey || ''} onChange={value => onElement({ dataKey: value })} />}
            {element.type === 'TEXT' && !element.dataKey && <label className="cg-field"><span>Texte fixe</span><textarea value={element.text || ''} onChange={event => onElement({ text: event.target.value })} /></label>}
            {/* Texte autour de la valeur, dans le meme calque : il ne s'affiche
                que si la variable a une valeur. Espaces compris. */}
            {element.type === 'TEXT' && element.dataKey && (
              <div className="cg-field-grid">
                <Field label="Texte avant" value={element.textPrefix || ''} onChange={value => onElement({ textPrefix: value })} />
                <Field label="Texte apres" value={element.textSuffix || ''} onChange={value => onElement({ textSuffix: value })} />
              </div>
            )}
            {(element.type === 'IMAGE' || element.type === 'VIDEO') && (
              <label className="cg-field"><span>{element.type === 'VIDEO' ? 'Source video' : 'Source image'}</span>
                <select value={element.assetId || ''} onChange={event => selectMediaSource(event.target.value)}>
                  <option value="">URL / ressource</option>
                  {element.assetId && !selectedAsset && <option value={element.assetId}>Asset introuvable</option>}
                  {compatibleAssets.map(asset => <option key={asset.id} value={asset.id}>{asset.name}</option>)}
                </select>
              </label>
            )}
            {element.type === 'IMAGE' && !element.assetId && <Field label="URL / ressource" value={element.imageUrl || ''} onChange={value => onElement({ imageUrl: value })} />}
            {element.type === 'VIDEO' && !element.assetId && <Field label="URL / ressource" value={element.videoUrl || ''} onChange={value => onElement({ videoUrl: value })} />}
            {(element.type === 'IMAGE' || element.type === 'VIDEO') && element.assetId && (
              <div className="cg-asset-reference">
                <span><Library size={13} /><strong>{selectedAsset?.name || 'Asset introuvable'}</strong></span>
                <button type="button" className="cg-button" onClick={() => onElement(element.type === 'VIDEO' ? { assetId: undefined, videoUrl: '' } : { assetId: undefined, imageUrl: '' })}>Dissocier</button>
              </div>
            )}
          </section>
        )}

        {(element.type === 'TEXT' || element.type === 'COUNTER') && (
          <section className="cg-form-section">
            <h3><Type size={13} />Typographie</h3>
            <label className="cg-field"><span>Police</span><select value={element.fontFamily || FONT_OPTIONS[0]} onChange={event => onElement({ fontFamily: event.target.value })}>
              <optgroup label="Systeme">{FONT_OPTIONS.map(font => <option key={font} value={font}>{font.split(',')[0]}</option>)}</optgroup>
              {customFonts.length > 0 && <optgroup label="Personnalisees">{customFonts.map(font => <option key={font.id} value={font.fontFamily}>{font.name}</option>)}</optgroup>}
              {/* La police stockee peut avoir ete supprimee de la bibliotheque. */}
              {element.fontFamily && !FONT_OPTIONS.includes(element.fontFamily) && !customFonts.some(font => font.fontFamily === element.fontFamily) && <option value={element.fontFamily}>Police introuvable</option>}
            </select></label>
            <div className="cg-field-grid three">
              <Field label="Taille" type="number" value={element.fontSize || 42} onChange={value => onElement({ fontSize: Number(value) })} />
              <Field label="Graisse" type="number" min={100} max={900} step={100} value={element.fontWeight || 700} onChange={value => onElement({ fontWeight: Number(value) })} />
              <Field label="Interlettrage" type="number" step={0.5} value={element.letterSpacing || 0} onChange={value => onElement({ letterSpacing: Number(value) })} />
            </div>
            <SelectField label="Alignement" value={element.textAlign || 'left'} options={[{ value: 'left', label: 'Gauche' }, { value: 'center', label: 'Centre' }, { value: 'right', label: 'Droite' }]} onChange={value => onElement({ textAlign: value as CgDesignerElement['textAlign'] })} />
            <ColorField label="Couleur" value={element.color || 'white'} onChange={value => onElement({ color: value })} />
          </section>
        )}

        {element.type === 'TEXT' && (
          <section className="cg-form-section">
            <h3><ArrowLeftRight size={13} />Defilement</h3>
            <label className="cg-check cg-toggle">
              <input type="checkbox" checked={Boolean(element.scroll)} onChange={event => onElement({ scroll: event.target.checked })} />
              <span aria-hidden="true" />Texte defilant (bandeau d'alerte)
            </label>
            {element.scroll && (
              <>
                <SelectField label="Sens" value={element.scrollDirection || 'left'} options={[{ value: 'left', label: 'Vers la gauche' }, { value: 'right', label: 'Vers la droite' }]} onChange={value => onElement({ scrollDirection: value as CgDesignerElement['scrollDirection'] })} />
                <div className="cg-field-grid">
                  <Field label="Vitesse (px/s)" type="number" min={10} max={2000} value={element.scrollSpeedPxSec ?? 120} onChange={value => onElement({ scrollSpeedPxSec: Number(value) })} />
                  <Field label="Ecart de boucle (px)" type="number" min={0} max={2000} value={element.scrollGapPx ?? 80} onChange={value => onElement({ scrollGapPx: Number(value) })} />
                </div>
              </>
            )}
          </section>
        )}

        {element.type === 'COUNTER' && (
          <section className="cg-form-section">
            <h3><Clock3 size={13} />Compteur</h3>
            <SelectField label="Mode" value={element.counterMode || 'COUNTDOWN'} options={[{ value: 'COUNTDOWN', label: 'Compte a rebours' }, { value: 'COUNTUP', label: 'Temps ecoule' }, { value: 'CLOCK', label: 'Horloge' }]} onChange={value => onElement({ counterMode: value as CgDesignerCounterMode })} />
            <SelectField label="Format" value={element.counterFormat || 'D_H_M_S'} options={[{ value: 'D_H_M_S', label: 'Jours + HH:MM:SS' }, { value: 'HH_MM_SS', label: 'HH:MM:SS' }, { value: 'HH_MM', label: 'HH:MM' }, { value: 'MM_SS', label: 'MM:SS (minutes totales)' }, { value: 'DAYS', label: 'J-7' }, { value: 'MINUTES', label: 'Minutes' }, { value: 'DATE_TIME', label: 'Date et heure' }]} onChange={value => onElement({ counterFormat: value as CgDesignerCounterFormat })} />
            {element.counterMode !== 'CLOCK' && (
              <Field
                label={element.dataKey ? 'Date fixe (remplace la variable)' : 'Date fixe'}
                type="datetime-local"
                value={isoToParisDateTimeInput(element.counterTarget)}
                onChange={value => onElement({ counterTarget: parisDateTimeInputToIso(value), dataKey: '' })}
              />
            )}
            <div className="cg-field-grid"><Field label="Prefixe" value={element.counterPrefix || ''} onChange={value => onElement({ counterPrefix: value })} /><Field label="Suffixe" value={element.counterSuffix || ''} onChange={value => onElement({ counterSuffix: value })} /></div>
          </section>
        )}

        {element.type === 'VIDEO' && (
          <section className="cg-form-section">
            <h3><Film size={13} />Lecture video</h3>
            <label className="cg-check cg-toggle">
              <input
                type="checkbox"
                checked={videoHoldEnabled}
                onChange={event => onElement(event.target.checked
                  ? {
                      videoHoldFrame: Math.max(1, element.videoHoldFrame || Math.round(design.fps || 25)),
                      videoFrameRate: element.videoFrameRate || design.fps || 25,
                      videoResumeBeforeEndMs: element.videoResumeBeforeEndMs ?? 1000,
                      videoLoop: false,
                    }
                  : { videoHoldFrame: 0 })}
              />
              <span aria-hidden="true" />Pause intermediaire
            </label>
            {!videoHoldEnabled && <label className="cg-check cg-toggle"><input type="checkbox" checked={element.videoLoop !== false} onChange={event => onElement({ videoLoop: event.target.checked })} /><span aria-hidden="true" />Lecture en boucle</label>}
            <Field label="Vitesse" type="number" min={0.1} max={4} step={0.1} value={element.videoPlaybackRate || 1} onChange={value => onElement({ videoPlaybackRate: Number(value) })} />
            {videoHoldEnabled && (
              <div className="cg-field-grid three">
                <Field label="Frame de pause" type="number" min={1} max={1000000} step={1} value={element.videoHoldFrame || 1} onChange={value => onElement({ videoHoldFrame: Math.max(1, Math.round(Number(value))) })} />
                <Field label="FPS source" type="number" min={1} max={120} step={0.01} value={element.videoFrameRate || design.fps || 25} onChange={value => onElement({ videoFrameRate: Number(value) })} />
                <Field label="Reprise avant retrait (ms)" type="number" min={0} max={3600000} step={10} value={element.videoResumeBeforeEndMs ?? 1000} onChange={value => onElement({ videoResumeBeforeEndMs: Number(value) })} />
              </div>
            )}
          </section>
        )}

        <section className="cg-form-section">
          <h3><Paintbrush size={13} />Aspect</h3>
          <ColorField label="Fond" value={element.background || 'transparent'} onChange={value => onElement({ background: value })} />
          <div className="cg-field-grid three">
            <Field label="Arrondi" type="number" value={element.borderRadius || 0} onChange={value => onElement({ borderRadius: Number(value) })} />
            <Field label="Contour" type="number" value={element.borderWidth || 0} onChange={value => onElement({ borderWidth: Number(value) })} />
            <Field label="Flou ombre" type="number" value={element.shadowBlur || 0} onChange={value => onElement({ shadowBlur: Number(value) })} />
          </div>
          <ColorField label="Contour" value={element.borderColor || 'transparent'} onChange={value => onElement({ borderColor: value })} />
          <ColorField label="Ombre" value={element.shadowColor || 'transparent'} onChange={value => onElement({ shadowColor: value })} />
          {(element.type === 'IMAGE' || element.type === 'VIDEO') && <SelectField label="Ajustement" value={element.imageFit || (element.type === 'VIDEO' ? 'contain' : 'cover')} options={[{ value: 'cover', label: 'Couvrir' }, { value: 'contain', label: 'Contenir' }, { value: 'fill', label: 'Etirer' }]} onChange={value => onElement({ imageFit: value as CgDesignerElement['imageFit'] })} />}
        </section>

        {(element.type === 'TEXT' || element.type === 'COUNTER') && (
          <section className="cg-form-section">
            <h3><Type size={13} />Aspect du texte</h3>
            <p className="cg-settings-hint">Contour et ombre appliques aux lettres, en plus de ceux de la box.</p>
            <div className="cg-field-grid three">
              <Field label="Contour lettres" type="number" min={0} value={element.textStrokeWidth || 0} onChange={value => onElement({ textStrokeWidth: Number(value) })} />
              <Field label="Decalage X" type="number" value={element.textShadowX || 0} onChange={value => onElement({ textShadowX: Number(value) })} />
              <Field label="Decalage Y" type="number" value={element.textShadowY || 0} onChange={value => onElement({ textShadowY: Number(value) })} />
            </div>
            <Field label="Flou ombre lettres" type="number" min={0} value={element.textShadowBlur || 0} onChange={value => onElement({ textShadowBlur: Number(value) })} />
            <ColorField label="Contour lettres" value={element.textStrokeColor || 'transparent'} onChange={value => onElement({ textStrokeColor: value })} />
            <ColorField label="Ombre lettres" value={element.textShadowColor || 'transparent'} onChange={value => onElement({ textShadowColor: value })} />
          </section>
        )}

        <ElementTimingSection element={element} onElement={onElement} />

        <section className="cg-form-section">
          <h3><WandSparkles size={13} />Animation</h3>
          <SelectField label="Entree" value={element.enterAnimation || 'NONE'} options={ANIMATION_OPTIONS} onChange={value => onElement({ enterAnimation: value as CgDesignerAnimation })} />
          <div className="cg-field-grid">
            <Field label="Delai (ms)" type="number" value={element.delayMs || 0} onChange={value => onElement({ delayMs: Number(value) })} />
            <Field label="Duree (ms)" type="number" value={element.durationMs || 450} onChange={value => onElement({ durationMs: Number(value) })} />
          </div>
          <SelectField label="Sortie" value={element.exitAnimation || 'FADE'} options={ANIMATION_OPTIONS} onChange={value => onElement({ exitAnimation: value as CgDesignerAnimation })} />
          <div className="cg-field-grid">
            <Field label="Delai sortie" type="number" value={element.exitDelayMs || 0} onChange={value => onElement({ exitDelayMs: Number(value) })} />
            <Field label="Duree sortie" type="number" value={element.exitDurationMs || 300} onChange={value => onElement({ exitDurationMs: Number(value) })} />
          </div>
          <SelectField label="Courbe" value={element.easing || 'EASE_OUT'} options={EASING_OPTIONS} onChange={value => onElement({ easing: value as CgDesignerEasing })} />
        </section>
      </fieldset>
    </aside>
  )
})

interface TimelineProps {
  elements: CgDesignerElement[]
  durationMs: number
  selectedId: string | null
  playheadMs: number
  previewMode: PreviewMode
  onSelect: (id: string) => void
  onPlayhead: (value: number) => void
  onPreview: (mode: PreviewMode) => void
}

type TimingMode = 'always' | 'once' | 'repeat'

const TIMING_OPTIONS: Array<{ value: TimingMode; label: string }> = [
  { value: 'always', label: "Jusqu'au retrait de l'habillage" },
  { value: 'once', label: 'Pendant une durée, une seule fois' },
  { value: 'repeat', label: 'Pendant une durée, à intervalle régulier' },
]

const toSeconds = (ms: number | undefined) => Number(((ms ?? 0) / 1000).toFixed(2))
const toMs = (value: string) => Math.max(0, Math.round(Number(value) * 1000))

/**
 * Temps d'affichage d'un element, compte depuis l'apparition de l'habillage :
 * il entre au moment choisi, reste la duree reglee, joue sa sortie, et revient
 * eventuellement a intervalle. Sans duree : rendu historique.
 */
const ElementTimingSection = memo(function ElementTimingSection({ element, onElement }: { element: CgDesignerElement; onElement: (patch: Partial<CgDesignerElement>) => void }) {
  const mode: TimingMode = !(element.visibleForMs ?? 0) ? 'always' : (element.repeatEveryMs ?? 0) > 0 ? 'repeat' : 'once'
  const changeMode = useCallback((value: string) => {
    if (value === 'always') onElement({ visibleForMs: 0, repeatEveryMs: 0 })
    else if (value === 'once') onElement({ visibleForMs: element.visibleForMs || 5000, repeatEveryMs: 0 })
    else onElement({ visibleForMs: element.visibleForMs || 5000, repeatEveryMs: element.repeatEveryMs || 30000 })
  }, [element.repeatEveryMs, element.visibleForMs, onElement])
  return (
    <section className="cg-form-section">
      <h3><Timer size={13} />Temps d'affichage</h3>
      <SelectField label="Affichage" value={mode} options={TIMING_OPTIONS} onChange={changeMode} />
      {mode !== 'always' && (
        <div className="cg-field-grid">
          <Field label="Apparaît à (s)" type="number" min={0} step={0.1} value={toSeconds(element.delayMs)} onChange={value => onElement({ delayMs: toMs(value) })} />
          <Field label="Visible pendant (s)" type="number" min={0.1} step={0.1} value={toSeconds(element.visibleForMs)} onChange={value => onElement({ visibleForMs: Math.max(100, toMs(value)) })} />
        </div>
      )}
      {mode === 'repeat' && (
        <Field label="Revient toutes les (s)" type="number" min={1} step={1} value={toSeconds(element.repeatEveryMs)} onChange={value => onElement({ repeatEveryMs: Math.max(1000, toMs(value)) })} />
      )}
      <div className="cg-timing-summary"><Timer size={13} /><span>{timingSummary(element)} Compté depuis l'apparition de l'habillage.</span></div>
    </section>
  )
})

const TimelinePanel = memo(function TimelinePanel(props: TimelineProps) {
  const duration = Math.max(250, props.durationMs)
  return (
    <section className="cg-timeline">
      <div className="cg-timeline-toolbar">
        <strong><Sparkles size={14} />Timeline</strong>
        <button type="button" className={`cg-button${props.previewMode === 'enter' ? ' is-primary' : ''}`} onClick={() => props.onPreview('enter')}><Play size={13} />Entrée</button>
        <button type="button" className={`cg-button${props.previewMode === 'exit' ? ' is-primary' : ''}`} onClick={() => props.onPreview('exit')}><RotateCcw size={13} />Sortie</button>
        <input type="range" min="0" max={duration} value={Math.min(props.playheadMs, duration)} onChange={event => props.onPlayhead(Number(event.target.value))} aria-label="Tete de lecture" title="Aperçu à cet instant depuis le début de la phase sélectionnée (entrée ou sortie)" />
        <span>{(props.playheadMs / 1000).toFixed(2)}s / {(duration / 1000).toFixed(2)}s</span>
      </div>
      <div className="cg-timeline-ruler"><span>0s</span><span>{(duration * .25 / 1000).toFixed(1)}s</span><span>{(duration * .5 / 1000).toFixed(1)}s</span><span>{(duration * .75 / 1000).toFixed(1)}s</span><span>{(duration / 1000).toFixed(1)}s</span></div>
      <div className="cg-timeline-tracks">
        {[...props.elements].reverse().map(element => {
          const enterLeft = clamp(((element.delayMs || 0) / duration) * 100, 0, 100)
          const enterWidth = clamp(((element.durationMs || 450) / duration) * 100, 0.5, 100 - enterLeft)
          const exitWidth = clamp(((element.exitDurationMs || 300) / duration) * 100, 0.5, 100)
          const exitRight = clamp(((element.exitDelayMs || 0) / duration) * 100, 0, 100 - exitWidth)
          return (
            <button type="button" key={element.id} className={`cg-timeline-track${props.selectedId === element.id ? ' is-selected' : ''}`} onClick={() => props.onSelect(element.id)}>
              <strong>{element.name}</strong>
              <span className="cg-track-area">
                {(element.visibleForMs ?? 0) > 0 && visibilityWindows(element, duration).map(window => (
                  <i key={window.from} className="cg-visible-bar" style={{ left: `${clamp((window.from / duration) * 100, 0, 100)}%`, width: `${clamp(((window.to - window.from) / duration) * 100, 0.5, 100)}%` }} />
                ))}
                <i className="cg-enter-bar" style={{ left: `${enterLeft}%`, width: `${enterWidth}%` }} />
                <i className="cg-exit-bar" style={{ right: `${exitRight}%`, width: `${exitWidth}%` }} />
                <i className="cg-playhead" style={{ left: `${clamp((props.playheadMs / duration) * 100, 0, 100)}%` }} />
              </span>
            </button>
          )
        })}
      </div>
    </section>
  )
})

interface Props {
  active?: boolean
  onLeaveGuard?: (guard: () => boolean) => void
  channelId: string
  assetsVersion: number
  fontsVersion?: number
  templatesVersion?: number
  openTemplateId?: string | null
  /** Sortie vers l'onglet appelant. Remplace le retour a la liste interne. */
  onExit?: () => void
  hideLogoTemplates?: boolean
  onUseTemplate: (template: CgTemplate) => void
  onAssetUsageChange: (assetIds: string[]) => void
  onAssetsChanged: () => void
  onTemplatesChanged: () => void
}

export const CgTemplateDesigner = memo(function CgTemplateDesigner({ active = true, onLeaveGuard, channelId, assetsVersion, fontsVersion = 0, templatesVersion = 0, openTemplateId, onExit, hideLogoTemplates = false, onUseTemplate, onAssetUsageChange, onAssetsChanged, onTemplatesChanged }: Props) {
  const [templates, setTemplates] = useState<CgTemplate[]>([])
  const [logoTemplateIds, setLogoTemplateIds] = useState<Set<string>>(() => new Set())
  const [assets, setAssets] = useState<CgAsset[]>([])
  const [fonts, setFonts] = useState<CgFont[]>([])
  const [screen, setScreen] = useState<'templates' | 'editor'>('templates')
  const [settingsTemplateId, setSettingsTemplateId] = useState<string | null>(null)
  const [layerOrderOpen, setLayerOrderOpen] = useState(false)
  const [hasEditorSession, setHasEditorSession] = useState(false)
  const [templateId, setTemplateId] = useState<string | null>(null)
  const [name, setName] = useState('Nouvel habillage')
  const [slug, setSlug] = useState('')
  const [design, setDesign] = useState<CgDesignerDesign>(() => blankDesign())
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [zoom, setZoom] = useState(0.4)
  const [snapEnabled, setSnapEnabled] = useState(true)
  const [previewRun, setPreviewRun] = useState(0)
  const [previewMode, setPreviewMode] = useState<PreviewMode>('enter')
  const [playheadMs, setPlayheadMs] = useState(0)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [historyVersion, setHistoryVersion] = useState(0)
  // Le canal de test ne montre que ce qui est reellement enregistre : tant
  // qu'une modification n'a pas ete sauvegardee, son lien reste inaccessible.
  const [dirty, setDirty] = useState(true)
  const [scrubbing, setScrubbing] = useState(false)
  const [versions, setVersions] = useState<Awaited<ReturnType<typeof cgTemplatesApi.history>> | null>(null)
  const tracker = useRef(new CgSaveTracker())
  const serverRevision = useRef<string | undefined>()
  const savingRef = useRef(false)
  const lastHistory = useRef({ key: '', at: 0 })
  const sessionDocument = useRef({ name, slug, templateId, dirty, hasEditorSession })
  sessionDocument.current = { name, slug, templateId, dirty, hasEditorSession }
  const rootRef = useRef<HTMLDivElement>(null)
  const endTransform = useRef<(() => void) | null>(null)
  useEffect(() => { if (!active) endTransform.current?.(); return () => endTransform.current?.() }, [active])
  const designRef = useRef(design)
  const pastRef = useRef<CgEditorSnapshot[]>([])
  const futureRef = useRef<CgEditorSnapshot[]>([])
  const handledOpenTemplateIdRef = useRef<string | null>(null)
  // Vrai quand la session d'edition en cours a ete ouverte depuis un autre
  // onglet (edition du template d'un logo) : sa fermeture doit rendre l'onglet
  // a sa liste de templates.
  const externalSessionRef = useRef(false)
  const workspaceRef = useRef<HTMLDivElement>(null)

  const persistDraft = useCallback(() => {
    const current = sessionDocument.current
    if (!current.hasEditorSession) return
    try {
      const key = cgDraftKey(channelId, current.templateId)
      if (!current.dirty) { localStorage.removeItem(key); return }
      localStorage.setItem(key, JSON.stringify({ name: current.name, slug: current.slug, design: designRef.current, expectedRevision: serverRevision.current, savedAt: new Date().toISOString() }))
    } catch { setMessage('Brouillon local indisponible : enregistrez avant de quitter cet écran.') }
  }, [channelId])
  useEffect(() => {
    const timer = window.setTimeout(persistDraft, 300)
    return () => window.clearTimeout(timer)
  }, [historyVersion, name, slug, dirty, persistDraft])
  useEffect(() => {
    const onUnload = (event: BeforeUnloadEvent) => {
      persistDraft()
      if (sessionDocument.current.hasEditorSession && sessionDocument.current.dirty) { event.preventDefault(); event.returnValue = '' }
    }
    window.addEventListener('beforeunload', onUnload)
    return () => { window.removeEventListener('beforeunload', onUnload); persistDraft(); tracker.current.open() }
  }, [persistDraft])
  const canLeave = useCallback(() => {
    persistDraft()
    return !sessionDocument.current.dirty || !sessionDocument.current.hasEditorSession || window.confirm('Des modifications ne sont pas enregistrées. Quitter cet éditeur ? Le brouillon sera conservé sur ce poste.')
  }, [persistDraft])
  useEffect(() => { onLeaveGuard?.(canLeave); return () => onLeaveGuard?.(() => true) }, [canLeave, onLeaveGuard])
  const markDirty = useCallback(() => { tracker.current.edit(); setDirty(true); setMessage('Modifications non enregistrées') }, [])
  const captureDocument = useCallback((): CgEditorSnapshot => ({ name: sessionDocument.current.name, slug: sessionDocument.current.slug, design: designRef.current }), [])
  const recordHistory = useCallback((key = '') => {
    const now = Date.now()
    if (!key || key !== lastHistory.current.key || now - lastHistory.current.at > 600) {
      pastRef.current = [...pastRef.current.slice(-99), captureDocument()]
    }
    lastHistory.current = { key, at: now }
    futureRef.current = []
  }, [captureDocument])

  const loadTemplates = useCallback(async () => {
    const [list, logoProfiles] = await Promise.all([
      cgTemplatesApi.list(channelId),
      hideLogoTemplates ? logoProfilesApi.list(channelId, true).catch(() => []) : Promise.resolve([]),
    ])
    setTemplates(list.filter(template => template.active && template.templateType === 'CUSTOM'))
    setLogoTemplateIds(new Set(logoProfiles.flatMap(profile => profile.templateId ? [profile.templateId] : [])))
  }, [channelId, hideLogoTemplates])

  const loadAssets = useCallback(async () => {
    setAssets(await cgAssetsApi.list(channelId))
  }, [channelId])

  // Les polices sont globales (une seule bibliotheque, toutes chaines confondues).
  const loadFonts = useCallback(async () => {
    setFonts(await cgFontsApi.list())
  }, [])

  useEffect(() => { if (active) void loadTemplates().catch(error => setMessage(current => `${current} — Actualisation des modèles impossible : ${cgSaveError(error)}`)) }, [active, loadTemplates, templatesVersion])
  useEffect(() => { if (active) void loadAssets().catch(error => setMessage(current => `${current} — Actualisation des assets impossible : ${cgSaveError(error)}`)) }, [active, assetsVersion, loadAssets])
  useEffect(() => { if (active) void loadFonts().catch(error => setMessage(current => `${current} — Chargement des polices impossible : ${cgSaveError(error)}`)) }, [active, fontsVersion, loadFonts])
  const assetUsageKey = [...new Set(design.elements.flatMap(element => element.assetId ? [element.assetId] : []))].sort().join(',')
  useEffect(() => { onAssetUsageChange(assetUsageKey ? assetUsageKey.split(',') : []) }, [assetUsageKey, onAssetUsageChange])

  const applyDesign = useCallback((next: CgDesignerDesign, record = true) => {
    if (record) recordHistory()
    designRef.current = next
    setDesign(next)
    setHistoryVersion(version => version + 1)
  }, [recordHistory])

  const commitDesign = useCallback((updater: (current: CgDesignerDesign) => CgDesignerDesign, historyKey = '') => {
    const next = updater(designRef.current)
    if (next === designRef.current) return
    recordHistory(historyKey)
    applyDesign(next, false)
    markDirty()
  }, [applyDesign, markDirty, recordHistory])

  const handleNameChange = useCallback((value: string) => { recordHistory('name'); setName(value); setHistoryVersion(v => v + 1); markDirty() }, [markDirty, recordHistory])
  const handleSlugChange = useCallback((value: string) => { recordHistory('slug'); setSlug(value); setHistoryVersion(v => v + 1); markDirty() }, [markDirty, recordHistory])

  const resetProject = useCallback(() => {
    endTransform.current?.()
    persistDraft()
    tracker.current.open()
    serverRevision.current = undefined
    lastHistory.current = { key: '', at: 0 }
    setTemplateId(null)
    setName('Nouvel habillage')
    setSlug('')
    setSelectedId(null)
    pastRef.current = []
    futureRef.current = []
    applyDesign(blankDesign(), false)
    setMessage('')
    setHasEditorSession(false)
    setDirty(true)
  }, [applyDesign, persistDraft])

  const newProject = useCallback(() => {
    if (!canLeave()) return
    resetProject()
    setHasEditorSession(true)
    setScreen('editor')
    const recovered = readCgDraft(localStorage, cgDraftKey(channelId, null))
    if (recovered && window.confirm('Restaurer le brouillon du nouveau modèle ?')) {
      setName(recovered.name); setSlug(recovered.slug); applyDesign(recovered.design, false); markDirty()
    }
  }, [applyDesign, canLeave, channelId, markDirty, resetProject])

  const loadTemplate = useCallback(async (template: CgTemplate, restore = true) => {
    endTransform.current?.()
    persistDraft()
    tracker.current.open()
    const session = tracker.current.capture()
    setScreen('templates'); setMessage('Chargement de la version enregistrée…')
    try {
      const fresh = await cgTemplatesApi.get(template.id)
      if (!tracker.current.accepts(session)) return
      const recovered = restore ? readCgDraft(localStorage, cgDraftKey(channelId, fresh.id)) : null
      const recover = recovered && window.confirm('Un brouillon non enregistré existe sur ce poste. Le restaurer ?')
      const nextDesign = normalizeDesign(recover ? recovered.design : fresh.defaultData?.design)
      serverRevision.current = recover ? recovered.expectedRevision : fresh.revision
      setTemplateId(fresh.id)
      setTemplates(current => current.map(item => item.id === fresh.id ? fresh : item))
      setName(recover ? recovered.name : fresh.name)
      setSlug(recover ? recovered.slug : templateSlugFromPath(fresh.htmlPath))
      setSelectedId(nextDesign.elements[nextDesign.elements.length - 1]?.id ?? null)
      pastRef.current = []; futureRef.current = []; lastHistory.current = { key: '', at: 0 }
      applyDesign(nextDesign, false)
      setMessage(recover ? 'Brouillon restauré — modifications non enregistrées' : 'Version enregistrée chargée')
      setHasEditorSession(true); setScreen('editor'); setDirty(Boolean(recover)); setScrubbing(false)
      if (!recover) localStorage.removeItem(cgDraftKey(channelId, fresh.id))
    } catch (error) { if (tracker.current.accepts(session)) setMessage(cgSaveError(error)) }
  }, [applyDesign, channelId, persistDraft])

  const openTemplateFromHome = useCallback((template: CgTemplate) => {
    if (hasEditorSession && template.id === templateId) {
      setScreen('editor')
      return
    }
    if (canLeave()) void loadTemplate(template)
  }, [canLeave, hasEditorSession, loadTemplate, templateId])

  useEffect(() => {
    if (!openTemplateId) {
      handledOpenTemplateIdRef.current = null
      // Le parent a referme la session ouverte depuis un autre onglet : on
      // repart de la liste, sinon revenir ici reaffiche le template du logo.
      if (externalSessionRef.current) {
        externalSessionRef.current = false
        resetProject()
        setScreen('templates')
      }
      return
    }
    if (handledOpenTemplateIdRef.current === openTemplateId) return
    const template = templates.find(item => item.id === openTemplateId)
    if (template) {
      handledOpenTemplateIdRef.current = openTemplateId
      externalSessionRef.current = true
      loadTemplate(template)
    }
  }, [loadTemplate, openTemplateId, resetProject, templates])

  const addElement = useCallback((type: CgDesignerElementType) => {
    if (designRef.current.elements.length >= 150) { setMessage('Maximum 150 calques par modèle.'); return }
    const element = defaultElement(type)
    commitDesign(current => ({ ...current, elements: [...current.elements, element] }))
    setSelectedId(element.id)
  }, [commitDesign])

  const updateElement = useCallback((patch: Partial<CgDesignerElement>) => {
    if (!selectedId || designRef.current.elements.find(element => element.id === selectedId)?.locked) return
    commitDesign(current => ({ ...current, elements: current.elements.map(element => element.id === selectedId ? { ...element, ...patch } : element) }), `element:${selectedId}:${Object.keys(patch).sort().join(',')}`)
  }, [commitDesign, selectedId])

  const updateDesign = useCallback((patch: Partial<CgDesignerDesign>) => {
    commitDesign(current => ({ ...current, ...patch }), `design:${Object.keys(patch).join(',')}`)
  }, [commitDesign])

  const deleteSelected = useCallback(() => {
    if (!selectedId || designRef.current.elements.find(element => element.id === selectedId)?.locked) return
    commitDesign(current => ({ ...current, elements: current.elements.filter(element => element.id !== selectedId) }))
    setSelectedId(null)
  }, [commitDesign, selectedId])

  const duplicateSelected = useCallback(() => {
    const source = designRef.current.elements.find(element => element.id === selectedId)
    if (!source) return
    if (designRef.current.elements.length >= 150) { setMessage('Maximum 150 calques par modèle.'); return }
    const copy = { ...source, id: uid(source.type.toLowerCase()), name: `${source.name} copie`, x: source.x + 30, y: source.y + 30 }
    commitDesign(current => ({ ...current, elements: [...current.elements, copy] }))
    setSelectedId(copy.id)
  }, [commitDesign, selectedId])

  const toggleVisible = useCallback((id: string) => {
    commitDesign(current => ({ ...current, elements: current.elements.map(element => element.id === id && !element.locked ? { ...element, visible: element.visible === false } : element) }))
  }, [commitDesign])

  const toggleLocked = useCallback((id: string) => {
    commitDesign(current => ({ ...current, elements: current.elements.map(element => element.id === id ? { ...element, locked: !element.locked } : element) }))
  }, [commitDesign])

  const reorderLayer = useCallback((id: string, direction: -1 | 1) => {
    commitDesign(current => {
      const index = current.elements.findIndex(element => element.id === id)
      const nextIndex = clamp(index + direction, 0, current.elements.length - 1)
      if (index < 0 || nextIndex === index || current.elements[index].locked) return current
      const elements = [...current.elements]
      const [element] = elements.splice(index, 1)
      elements.splice(nextIndex, 0, element)
      return { ...current, elements }
    })
  }, [commitDesign])

  const restoreSnapshot = useCallback((snapshot: CgEditorSnapshot) => {
    setName(snapshot.name); setSlug(snapshot.slug); applyDesign(snapshot.design, false); markDirty()
    lastHistory.current = { key: '', at: 0 }
  }, [applyDesign, markDirty])
  const undo = useCallback(() => {
    const previous = pastRef.current.pop()
    if (!previous) return
    futureRef.current.push(captureDocument()); restoreSnapshot(previous)
  }, [captureDocument, restoreSnapshot])
  const redo = useCallback(() => {
    const next = futureRef.current.pop()
    if (!next) return
    pastRef.current.push(captureDocument()); restoreSnapshot(next)
  }, [captureDocument, restoreSnapshot])

  const transformElement = useCallback((id: string, mode: TransformMode, event: ReactPointerEvent) => {
    event.preventDefault()
    event.stopPropagation()
    endTransform.current?.()
    const initialDesign = designRef.current
    const initial = initialDesign.elements.find(element => element.id === id)
    if (!initial || initial.locked) return
    const startX = event.clientX
    const startY = event.clientY
    const centerX = initial.x * zoom + initial.width * zoom / 2
    const centerY = initial.y * zoom + initial.height * zoom / 2
    const stageRect = workspaceRef.current?.querySelector('.cg-canvas-stage')?.getBoundingClientRect()
    const initialAngle = Math.atan2(event.clientY - (stageRect?.top || 0) - centerY, event.clientX - (stageRect?.left || 0) - centerX)
    let changed = false
    let raf: number | null = null
    const initialSnapshot = captureDocument()

    const snapValue = (value: number) => snapEnabled ? Math.round(value / (initialDesign.gridSize || 10)) * (initialDesign.gridSize || 10) : value
    const onMove = (moveEvent: PointerEvent) => {
      const dx = (moveEvent.clientX - startX) / zoom
      const dy = (moveEvent.clientY - startY) / zoom
      let patch: Partial<CgDesignerElement>
      if (mode === 'resize') {
        patch = { width: Math.max(10, snapValue(initial.width + dx)), height: Math.max(10, snapValue(initial.height + dy)) }
      } else if (mode === 'rotate') {
        const angle = Math.atan2(moveEvent.clientY - (stageRect?.top || 0) - centerY, moveEvent.clientX - (stageRect?.left || 0) - centerX)
        patch = { rotation: Math.round((initial.rotation || 0) + (angle - initialAngle) * 180 / Math.PI) }
      } else {
        patch = { x: snapValue(initial.x + dx), y: snapValue(initial.y + dy) }
      }
      const next = { ...designRef.current, elements: designRef.current.elements.map(element => element.id === id ? { ...element, ...patch } : element) }
      designRef.current = next
      tracker.current.edit()
      if (raf === null) raf = requestAnimationFrame(() => { raf = null; setDesign(designRef.current) })
      if (!changed) markDirty()
      changed = true
    }
    const onUp = () => {
      endTransform.current = null
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      if (raf !== null) cancelAnimationFrame(raf)
      setDesign(designRef.current)
      if (changed) {
        pastRef.current = [...pastRef.current.slice(-99), initialSnapshot]
        lastHistory.current = { key: '', at: 0 }
        futureRef.current = []
        setHistoryVersion(version => version + 1)
      }
    }
    endTransform.current = onUp
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
  }, [captureDocument, markDirty, snapEnabled, zoom])

  const fitCanvas = useCallback(() => {
    const workspace = workspaceRef.current
    if (!workspace) return
    setZoom(clamp(Math.min((workspace.clientWidth - 80) / designRef.current.width, (workspace.clientHeight - 80) / designRef.current.height), 0.1, 1))
  }, [])

  const preview = useCallback((mode: PreviewMode) => {
    setScrubbing(false)
    setPreviewMode(mode)
    setPreviewRun(run => run + 1)
  }, [])

  const save = useCallback(async () => {
    if (savingRef.current || !name.trim()) return
    const invalidInput = rootRef.current?.querySelector<HTMLInputElement>('input:invalid, textarea:invalid, [aria-invalid="true"]')
    if (invalidInput) { invalidInput.reportValidity?.(); invalidInput.focus(); setMessage('Corrigez le champ signalé avant d’enregistrer.'); return }
    const invalid = validCgDesign(designRef.current)
    if (invalid) { setSelectedId(invalid.elementId ?? null); setMessage(invalid.message); return }
    const token = tracker.current.capture()
    savingRef.current = true; setSaving(true); setMessage('Enregistrement en cours…')
    persistDraft()
    try {
      const result = await cgTemplatesApi.saveDesigner({ channelId, templateId, name, slug: slug || null, design: designRef.current, expectedRevision: serverRevision.current })
      if (!tracker.current.accepts(token)) return
      serverRevision.current = result.template.revision
      setTemplateId(result.template.id)
      setTemplates(current => [...current.filter(item => item.id !== result.template.id), result.template])
      const clean = tracker.current.clean(token)
      if (clean) {
        setName(result.template.name)
        setSlug(templateSlugFromPath(result.htmlPath))
        const normalized = normalizeDesign(result.template.defaultData?.design)
        const selectedIndex = designRef.current.elements.findIndex(element => element.id === selectedId)
        applyDesign(normalized, false)
        setSelectedId(normalized.elements[selectedIndex]?.id ?? null)
        setDirty(false)
        setMessage(`Enregistré à ${new Date().toLocaleTimeString('fr-FR')} `)
        try { localStorage.removeItem(cgDraftKey(channelId, templateId)) } catch { /* stockage local indisponible */ }
      } else {
        // Creation may assign an ID while editing continues: migrate the draft key.
        setDirty(true)
        setMessage('Version envoyée enregistrée — nouvelles modifications à enregistrer')
        try { localStorage.setItem(cgDraftKey(channelId, result.template.id), JSON.stringify({ ...captureDocument(), expectedRevision: serverRevision.current, savedAt: new Date().toISOString() })); if (!templateId) localStorage.removeItem(cgDraftKey(channelId, null)) } catch { /* valeur ignoree volontairement */ }
      }
      onAssetsChanged(); onTemplatesChanged()
    } catch (error) {
      if (tracker.current.accepts(token)) { setMessage(cgSaveError(error)); persistDraft() }
    } finally { savingRef.current = false; setSaving(false) }
  }, [applyDesign, captureDocument, channelId, name, onAssetsChanged, onTemplatesChanged, persistDraft, selectedId, slug, templateId])

  const reloadSaved = useCallback(() => {
    const current = templates.find(item => item.id === templateId)
    if (current && canLeave()) void loadTemplate(current, false)
  }, [canLeave, loadTemplate, templateId, templates])
  const openHistory = useCallback(async () => {
    if (!templateId || savingRef.current) return
    const token = tracker.current.capture()
    try { const list = await cgTemplatesApi.history(templateId); if (tracker.current.accepts(token)) setVersions(list) }
    catch (error) { setMessage(cgSaveError(error)) }
  }, [templateId])
  const restoreVersion = useCallback(async (restored: CgDesignerDesign) => {
    if (!templateId || !canLeave()) return
    const token = tracker.current.capture()
    try {
      const latest = await cgTemplatesApi.get(templateId)
      if (!tracker.current.clean(token)) { setMessage('Le brouillon a changé : relancez la restauration.'); return }
      serverRevision.current = latest.revision
      setName(latest.name); setSlug(templateSlugFromPath(latest.htmlPath))
      applyDesign(normalizeDesign(restored)); markDirty(); setVersions(null)
      setMessage('Ancienne composition restaurée en brouillon — enregistrez pour l’appliquer')
    } catch (error) { setMessage(cgSaveError(error)) }
  }, [applyDesign, canLeave, markDirty, templateId])
  const closeHistory = useCallback(() => setVersions(null), [])
  const scrub = useCallback((ms: number) => { setScrubbing(true); setPlayheadMs(ms) }, [])

  const duplicateProject = useCallback(() => {
    persistDraft(); tracker.current.open(); serverRevision.current = undefined
    setTemplateId(null)
    setName(`${name} copie`)
    setSlug('')
    setMessage('Copie prete a enregistrer')
    setDirty(true)
  }, [name, persistDraft])

  const deleteProject = useCallback(async () => {
    if (!templateId || savingRef.current || !window.confirm(`Supprimer définitivement « ${name} » ?`)) return
    try {
      await cgTemplatesApi.delete(templateId)
      try { localStorage.removeItem(cgDraftKey(channelId, templateId)) } catch { /* stockage local indisponible */ }
      sessionDocument.current.hasEditorSession = false
      resetProject()
      await Promise.all([loadTemplates(), loadAssets()])
      onAssetsChanged()
      onTemplatesChanged()
      setMessage('Template supprime')
      setHasEditorSession(false)
      setScreen('templates')
    } catch (error: any) {
      setMessage(error.message ?? 'Suppression impossible')
    }
  }, [channelId, loadAssets, loadTemplates, name, resetProject, onAssetsChanged, onTemplatesChanged, templateId])

  const useTemplate = useCallback(() => {
    const template = templates.find(item => item.id === templateId)
    if (template && canLeave()) onUseTemplate(template)
  }, [canLeave, onUseTemplate, templateId, templates])

  const canOpenTestChannel = useHasPermission('TECHNICAL_SETTINGS_MANAGE')
  const openTestChannel = useCallback(() => openSettingsTab('test-channel'), [])

  useEffect(() => {
    if (!active || screen !== 'editor') return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || document.querySelector('[role=dialog]')) return
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') { event.preventDefault(); void save(); return }
      const target = event.target as HTMLElement | null
      if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) return
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') { event.preventDefault(); if (event.shiftKey) redo(); else undo(); return }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y') { event.preventDefault(); redo(); return }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'd') { event.preventDefault(); duplicateSelected(); return }
      if (event.key === 'Delete' || event.key === 'Backspace') { event.preventDefault(); deleteSelected(); return }
      if (designRef.current.elements.find(element => element.id === selectedId)?.locked) return
      if (!selectedId || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return
      event.preventDefault()
      const step = event.shiftKey ? 10 : 1
      const dx = event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0
      const dy = event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0
      commitDesign(current => ({ ...current, elements: current.elements.map(element => element.id === selectedId ? { ...element, x: element.x + dx, y: element.y + dy } : element) }))
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [active, screen, save, commitDesign, deleteSelected, duplicateSelected, redo, selectedId, undo])

  const selectedElement = useMemo(() => design.elements.find(element => element.id === selectedId) ?? null, [design.elements, selectedId])
  const visibleTemplates = useMemo(
    () => hideLogoTemplates ? templates.filter(template => !logoTemplateIds.has(template.id) && !isLogoTemplatePath(template.htmlPath)) : templates,
    [hideLogoTemplates, logoTemplateIds, templates],
  )
  const canUndo = useMemo(() => pastRef.current.length > 0, [historyVersion])
  const canRedo = useMemo(() => futureRef.current.length > 0, [historyVersion])
  // Quand le designer a ete ouvert depuis un autre onglet (l'edition d'un logo,
  // par exemple), quitter l'editeur doit ramener a cet onglet-la et non a la
  // liste interne des templates : l'operateur n'a rien a y faire, il venait des
  // logos et veut y retourner.
  const backToTemplates = useCallback(() => {
    if (!canLeave()) return
    if (onExit) onExit()
    else setScreen('templates')
  }, [canLeave, onExit])
  const resumeEditor = useCallback(() => setScreen('editor'), [])

  const settingsTemplate = useMemo(
    () => templates.find(template => template.id === settingsTemplateId) ?? null,
    [settingsTemplateId, templates],
  )
  // Charge les polices uploadees dans l'editeur pour que l'apercu du canvas
  // rende exactement la meme typo qu'a l'antenne (memes @font-face). Ce memo
  // doit rester au-dessus du retour anticipe de l'ecran "templates", sinon le
  // nombre de hooks change entre les deux ecrans et React plante (page blanche).
  const fontFaceStyle = useMemo(() => fonts
    .map(font => {
      const format = font.mimeType === 'font/woff2' ? 'woff2' : font.mimeType === 'font/otf' ? 'opentype' : 'truetype'
      return `@font-face { font-family: '${font.fontFamily}'; src: url('${font.url}') format('${format}'); font-display: block; }`
    })
    .join('\n'), [fonts])

  const layeredTemplateCount = useMemo(() => visibleTemplates.filter(template => template.cgLayer !== null).length, [visibleTemplates])
  const openLayerOrder = useCallback(() => setLayerOrderOpen(true), [])
  const closeLayerOrder = useCallback(() => setLayerOrderOpen(false), [])
  const layerOrderSaved = useCallback((result: CgLayerOrderResult) => {
    setMessage(result.moved === 0
      ? 'Superposition inchangée.'
      : result.swappedOnAir > 0
        ? `Superposition appliquée : ${result.swappedOnAir === 1 ? 'un habillage a changé' : `${result.swappedOnAir} échanges ont changé les habillages`} de place à l’antenne.`
        : 'Superposition enregistrée : elle s’appliquera aux prochains affichages.')
    void loadTemplates()
    onTemplatesChanged()
  }, [loadTemplates, onTemplatesChanged])
  const openSettings = useCallback((template: CgTemplate) => setSettingsTemplateId(template.id), [])
  const closeSettings = useCallback(() => setSettingsTemplateId(null), [])
  const saveSettings = useCallback(async (draft: CgSettingsDraft) => {
    if (!settingsTemplate) return
    const saved = await cgTemplatesApi.updateSettings(settingsTemplate.id, { name: draft.name, slug: draft.slug, manualOnGrid: draft.manualOnGrid, expectedRevision: draft.expectedRevision })
    // La session d'edition ouverte pointe le meme modele : sans ca, le prochain
    // enregistrement du studio reecrirait l'ancien identifiant.
    if (settingsTemplate.id === templateId) {
      setName(draft.name)
      setSlug(templateSlugFromPath(saved.htmlPath))
      serverRevision.current = saved.revision
    }
    await loadTemplates()
    onTemplatesChanged()
  }, [loadTemplates, onTemplatesChanged, settingsTemplate, templateId])

  if (screen === 'templates') {
    return (
      <>
        {message && <p role="status" className="cg-toolbar-message">{message}</p>}
        <TemplatesPanel
          templates={visibleTemplates}
          templateId={templateId}
          canResume={hasEditorSession}
          onLoad={openTemplateFromHome}
          onNew={newProject}
          onResume={resumeEditor}
          onSettings={openSettings}
          onLayerOrder={layeredTemplateCount >= 2 ? openLayerOrder : undefined}
        />
        {layerOrderOpen && (
          <CgLayerOrderDialog channelId={channelId} templates={visibleTemplates} onClose={closeLayerOrder} onSaved={layerOrderSaved} />
        )}
        {settingsTemplate && (
          <CgTemplateSettingsDialog
            title={`Réglages — ${settingsTemplate.name}`}
            initial={{ expectedRevision: settingsTemplate.revision, name: settingsTemplate.name, slug: templateSlugFromPath(settingsTemplate.htmlPath), manualOnGrid: settingsTemplate.manualOnGrid }}
            htmlPath={settingsTemplate.htmlPath}
            cgLayer={settingsTemplate.cgLayer}
            onClose={closeSettings}
            onSubmit={saveSettings}
          />
        )}
      </>
    )
  }

  return (
    <div ref={rootRef} className="cg-studio">
      {fontFaceStyle && <style>{fontFaceStyle}</style>}
      <StudioToolbar
        name={name}
        slug={slug}
        saving={saving}
        canUndo={canUndo}
        canRedo={canRedo}
        canUse={Boolean(templateId)}
        canTest={Boolean(templateId) && !dirty && !saving}
        canOpenTestChannel={canOpenTestChannel}
        message={message}
        onName={handleNameChange}
        onSlug={handleSlugChange}
        onBack={backToTemplates}
        onNew={newProject}
        onSave={save}
        onReload={reloadSaved}
        onHistory={openHistory}
        onDuplicateProject={duplicateProject}
        onDeleteProject={deleteProject}
        onUndo={undo}
        onRedo={redo}
        onUse={useTemplate}
        onOpenTestChannel={openTestChannel}
      />
      {versions && <ModalShell title="Versions enregistrées" size="narrow" onClose={closeHistory}>
        <p className="cg-settings-hint">Restaurer une composition crée un brouillon. Elle ne sera appliquée qu’après enregistrement.</p>
        <div className="cg-history-list">
          {versions.length === 0 && <p>Aucune version précédente enregistrée.</p>}
          {versions.map(version => <Button key={version.revision} variant="outline" onClick={() => void restoreVersion(version.design)}>{new Date(version.savedAt).toLocaleString('fr-FR')} — {version.name}</Button>)}
        </div>
      </ModalShell>}
      <div className="cg-studio-grid">
        <ResourcesPanel
          elements={design.elements}
          selectedId={selectedId}
          onAdd={addElement}
          onSelect={setSelectedId}
          onToggleVisible={toggleVisible}
          onToggleLocked={toggleLocked}
          onReorder={reorderLayer}
        />
        <CanvasPanel
          design={design}
          zoom={zoom}
          snapEnabled={snapEnabled}
          selectedId={selectedId}
          active={active}
          scrubMs={scrubbing ? playheadMs : null}
          previewRun={previewRun}
          previewMode={previewMode}
          onZoom={setZoom}
          onFit={fitCanvas}
          onSnap={setSnapEnabled}
          onSelect={setSelectedId}
          onTransform={transformElement}
          workspaceRef={workspaceRef}
        />
        <InspectorPanel element={selectedElement} design={design} assets={assets} fonts={fonts} onElement={updateElement} onDesign={updateDesign} onDuplicate={duplicateSelected} onDelete={deleteSelected} />
        <TimelinePanel elements={design.elements} durationMs={design.durationMs || 10000} selectedId={selectedId} playheadMs={playheadMs} previewMode={previewMode} onSelect={setSelectedId} onPlayhead={scrub} onPreview={preview} />
      </div>
    </div>
  )
})
